import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    FormControl,
    MenuItem,
    Select,
    TextField,
} from "@material-ui/core";
import { useSnackbar } from "notistack";
import React, { useEffect, useState } from "react";

import { AjaxResult } from "../../../../../enums/ajaxResult";
import { SiteCommissioningItem, SiteCommissioningSummary } from "../../../../../dtos/siteCommissioning";
import { FormField } from "../../../../shared/formField";
import { FormSection } from "../../../../shared/formSection";
import { ValidationErrors } from "../../../../shared/validationErrors";
import { useApi } from "../../../../useApi";

import { isPositiveInteger, isValidIPv4 } from "./validation";
import { DIALOG_LABEL_WIDTH } from "./dialogLayout";
import { humanizeDeviceType } from "./ioDeviceLabels";
import { removeIoDevice, splitTopologyItems } from "./siteTopologyEditing";

type DeviceType = "dmp" | "pz520relay" | "pz551wirelessreceiver";

const ADDABLE_DEVICE_TYPES: DeviceType[] = ["dmp", "pz520relay", "pz551wirelessreceiver"];

const DEFAULT_PZ520_RELAY_IP_ADDRESS = "192.168.1.75";

function defaultIpAddressFor(type: DeviceType): string {
    return type === "pz520relay" ? DEFAULT_PZ520_RELAY_IP_ADDRESS : "";
}

interface Props {
    open: boolean;
    onClose: () => void;
    siteId: number;
    existingItems: Record<string, SiteCommissioningItem>;
    editingKey?: string;
    onSaved: (summary: SiteCommissioningSummary) => void;
}

function keyFor(type: DeviceType, accountNumber: string): string {
    return type === "dmp" ? `ioDevice:dmp:${accountNumber}` : `ioDevice:${type}`;
}

export const EditIoDeviceDialog: React.FC<Props> = ({ open, onClose, siteId, existingItems, editingKey, onSaved }) => {
    const { siteApi } = useApi();
    const { enqueueSnackbar } = useSnackbar();
    const [type, setType] = useState<DeviceType>("dmp");
    const [accountNumber, setAccountNumber] = useState("");
    const [ipAddress, setIpAddress] = useState("");
    const [port, setPort] = useState("");
    const [errors, setErrors] = useState<string[]>([]);
    const [saving, setSaving] = useState(false);

    const isEditing = Boolean(editingKey);

    const typesUsedByOtherDevices = new Set(
        Object.keys(existingItems)
            .filter((key) => key.startsWith("ioDevice:") && key !== editingKey)
            .map((key) => key.split(":")[1])
    );
    const selectableTypes = ADDABLE_DEVICE_TYPES.filter((candidate) => !typesUsedByOtherDevices.has(candidate));

    const changeType = (newType: DeviceType) => {
        if (!isEditing && ipAddress === defaultIpAddressFor(type)) {
            setIpAddress(defaultIpAddressFor(newType));
        }
        setType(newType);
    };

    useEffect(() => {
        if (open) {
            const fields = editingKey ? existingItems[editingKey]?.fields : undefined;
            const initialType = editingKey
                ? (editingKey.startsWith("ioDevice:dmp:") ? "dmp" : (editingKey.split(":")[1] as DeviceType))
                : selectableTypes[0] ?? "dmp";
            setType(initialType);
            setAccountNumber(typeof fields?.accountNumber === "string" ? fields.accountNumber : "");
            setIpAddress(editingKey ? (typeof fields?.ipAddress === "string" ? fields.ipAddress : "") : defaultIpAddressFor(initialType));
            setPort(fields?.port !== undefined && fields?.port !== null ? String(fields.port) : "");
            setErrors([]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, editingKey]);

    const save = () => {
        const clientErrors: string[] = [];
        if (type === "dmp") {
            if (!isPositiveInteger(accountNumber) || Number(accountNumber) > 65535) {
                clientErrors.push("DMP panel account number must be a number between 1 and 65535.");
            }
            if (!isValidIPv4(ipAddress)) {
                clientErrors.push("DMP panel IP address must be a valid IPv4 address.");
            }
            if (!isPositiveInteger(port)) {
                clientErrors.push("DMP panel port is required and must be a positive number.");
            }
        } else if (type === "pz520relay" && ipAddress && !isValidIPv4(ipAddress)) {
            clientErrors.push("Relay IP address must be a valid IPv4 address.");
        }
        if (clientErrors.length > 0) {
            setErrors(clientErrors);
            return;
        }

        const newKey = keyFor(type, accountNumber);
        let items = editingKey ? removeIoDevice(existingItems, editingKey) : { ...existingItems };

        const deviceTypeAlreadyExists = Object.keys(items)
            .filter((key) => key.startsWith("ioDevice:"))
            .some((key) => key.split(":")[1] === type);
        if (deviceTypeAlreadyExists) {
            setErrors(["An I/O device of this type already exists. Edit it instead of adding a new one."]);
            return;
        }

        const existingFields = editingKey ? existingItems[editingKey]?.fields : undefined;
        items = {
            ...items,
            [newKey]:
                type === "dmp"
                    ? {
                          fields: {
                              accountNumber,
                              ipAddress,
                              port: Number(port),
                              remoteKeyEnabled: Boolean(existingFields?.remoteKeyEnabled),
                              lockoutCodeEnabled: Boolean(existingFields?.lockoutCodeEnabled),
                              auxiliaryIOEnabled: Boolean(existingFields?.auxiliaryIOEnabled),
                          },
                      }
                    : type === "pz520relay"
                    ? { fields: ipAddress ? { ipAddress } : {} }
                    : { fields: {} },
        };

        setSaving(true);
        const { ioDeviceItems, boxItems } = splitTopologyItems(items);
        siteApi
            .updateCommissioning(siteId, [
                { name: "ioDevices", items: ioDeviceItems },
                { name: "boxes", items: boxItems },
            ])
            .then((r) => {
                setSaving(false);
                if (r.result === AjaxResult.Success && r.data) {
                    onSaved(r.data);
                    onClose();
                    enqueueSnackbar(isEditing ? "I/O device updated" : "I/O device added", { variant: "success" });
                } else if (r.messages) {
                    setErrors(r.messages);
                } else {
                    enqueueSnackbar("ERROR: Failed to save the I/O device", { variant: "error" });
                }
            });
    };

    return (
        <Dialog open={open} disablePortal>
            <DialogContent style={{ minWidth: "400px" }}>
                <FormSection headerText={isEditing ? "Edit I/O Device" : "Add I/O Device"}>
                    <FormField labelWidth={DIALOG_LABEL_WIDTH} label="Type" required>
                        <FormControl style={{ flexGrow: 1 }}>
                            <Select value={type} onChange={(event) => changeType(event.target.value as DeviceType)} variant="outlined">
                                {selectableTypes.map((candidate) => (
                                    <MenuItem key={candidate} value={candidate}>{humanizeDeviceType(candidate)}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </FormField>

                    {type === "dmp" && (
                        <FormField labelWidth={DIALOG_LABEL_WIDTH} label="Account Number" required>
                            <FormControl style={{ flexGrow: 1 }}>
                                <TextField
                                    value={accountNumber}
                                    onChange={(event) => setAccountNumber(event.currentTarget.value)}
                                    variant="outlined"
                                    InputProps={{ inputProps: { style: { padding: "8px" } } }}
                                />
                            </FormControl>
                        </FormField>
                    )}

                    {(type === "dmp" || type === "pz520relay") && (
                        <FormField labelWidth={DIALOG_LABEL_WIDTH} label="IP Address" required={type === "dmp"}>
                            <FormControl style={{ flexGrow: 1 }}>
                                <TextField
                                    value={ipAddress}
                                    onChange={(event) => setIpAddress(event.currentTarget.value)}
                                    variant="outlined"
                                    InputProps={{ inputProps: { style: { padding: "8px" } } }}
                                />
                            </FormControl>
                        </FormField>
                    )}

                    {type === "dmp" && (
                        <FormField labelWidth={DIALOG_LABEL_WIDTH} label="Port" required>
                            <FormControl style={{ flexGrow: 1 }}>
                                <TextField
                                    value={port}
                                    onChange={(event) => setPort(event.currentTarget.value)}
                                    variant="outlined"
                                    InputProps={{ inputProps: { style: { padding: "8px" } } }}
                                />
                            </FormControl>
                        </FormField>
                    )}

                    <ValidationErrors errors={errors} />
                </FormSection>
            </DialogContent>
            <DialogActions>
                <Button color="primary" variant="contained" onClick={save} disabled={saving}>
                    {saving ? "Saving..." : "Save"}
                </Button>
                <Button color="secondary" variant="contained" onClick={onClose} disabled={saving}>
                    Cancel
                </Button>
            </DialogActions>
        </Dialog>
    );
};
